import {computed, ref, watch} from "@vue/composition-api";
import store from "@/store";
import {title} from "@core/utils/filter";

// Notification
import {useToast} from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default function useBidsList() {
    // Use toast
    const toast = useToast();

    const refBidListTable = ref(null);

    // Table Handlers
    const tableColumns = [
        {key: "offer_code", label: "Offre Code", sortable: true},
        {key: "brief"},
        {key: "type"},
        {
            key: "created_at",
            label: "Crée le",
            formatter: title,
            sortable: true,
        },
        {key: "actions"},
    ];
    const stateOptions = [
        {label: "Gagnée", value: "won"},
        {label: "Perdue", value: "lost"},
    ];
    const perPage = ref(10);
    const totalBids = ref(0);
    const currentPage = ref(1);
    const perPageOptions = [10, 25, 50, 100];
    const searchQuery = ref("");
    const customerQuery = ref("");
    const sortBy = ref("created_at");
    const isSortDirDesc = ref(true);
    const customerOptions = ref([]);
    const countryOptions = ref([]);
    const departmentsOptions = ref([]);
    const statusOptions = ref([]);
    const typeOptions = ref([]);
    const stateFilter = ref(null);
    const typeFilter = ref(null);
    const statusFilter = ref(null);

    const dataMeta = computed(() => {
        const localItemsCount = refBidListTable.value
            ? refBidListTable.value.localItems.length
            : 0;
        return {
            from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
            to: perPage.value * (currentPage.value - 1) + localItemsCount,
            of: totalBids.value,
        };
    });

    const bidStateClass = (bidInv) => {
        const {
            set_as_lost,
            is_closed
        } = bidInv
        if (set_as_lost) {
            return {class: "border border-1 border-danger rounded-pill text-danger", text: "Perdu"}
        } else if (is_closed) {
            return {class: "border border-1 border-secondary rounded-pill text-secondary", text: "Fermé"}
        }
        return {class: "", text: ""}
    };

    const refetchData = () => {
        refBidListTable.value.refresh();
    };

    watch(
        [
            currentPage,
            perPage,
            customerQuery,
            searchQuery,
            stateFilter,
            typeFilter,
            statusFilter,
        ],
        () => {
            refetchData();
        }
    );

    const toCapitalizeFirstLetter = (str) => {
        return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
    };

    // get all bid's properties to create or edit a bid
    const fetchBidProperties = (properties) => {
        let params = {
            props: properties,
        };
        store
            .dispatch("bid/fetchProperties", params)
            .then(({customers, countries, departments, status, types }) => {
                customerOptions.value = customers;
                countryOptions.value = countries;
                departmentsOptions.value = departments;
                statusOptions.value = status;
                typeOptions.value = types.map(currType => ({ label: toCapitalizeFirstLetter(currType), value: currType}));
            })
            .catch( () => {
                toast({
                    component: ToastificationContent,
                    props: {
                        title: "Erreur de récupération des données",
                        icon: "AlertTriangleIcon",
                        variant: "danger",
                    },
                });
            });
    };

    const fetchBids = (ctx, callback) => {
        store
            .dispatch("bid/fetchBids", {
                q: searchQuery.value,
                customer: customerQuery.value,
                perPage: perPage.value,
                page: currentPage.value,
                sortBy: sortBy.value,
                sortDesc: isSortDirDesc.value,
                type: typeFilter.value ?? "",
                status: statusFilter.value ?? "",
                state: stateFilter.value ?? "",
            })
            .then((response) => {
                const {bids, total} = response;

                let bidMapped = bids.map((p) => {
                    if (p.deleted_at) p._rowVariant = "danger";
                    return p;
                });

                callback(bidMapped);
                totalBids.value = total;
            })
            .catch(() => {
                toast({
                    component: ToastificationContent,
                    props: {
                        title: "Erreur de récupération des offres",
                        icon: "AlertTriangleIcon",
                        variant: "danger",
                    },
                });
            });
    };

    // *===============================================---*
    // *--------- UI ---------------------------------------*
    // *===============================================---*

    const resolveBidTypeVariant = (type) => {
        if (type === "étude") return "primary";
        if (type === "contrôle") return "info";
        if (type === "étude et contrôle") return "success";
        return "primary";
    };

    const resolveBidTypeIcon = (role) => {
        if (type === "étude") return "UserIcon";
        if (type === "contrôle") return "SettingsIcon";
        if (role === "étude et contrôle") return "ServerIcon";
        return "UserIcon";
    };

    const resolveBidStatusVariant = (status) => {
        if (status === "avis à manifestation d'interêt") return "warning";
        if (status === "demande de proposition") return "success";
        if (status === "réalisation") return "secondary";
        if (status === "négociation directe") return "info";
        return "primary";
    };


    return {
        fetchBids,
        fetchBidProperties,
        tableColumns,
        perPage,
        currentPage,
        totalBids,
        dataMeta,
        perPageOptions,
        searchQuery,
        customerQuery,
        sortBy,
        isSortDirDesc,
        refBidListTable,

        resolveBidTypeVariant,
        resolveBidTypeIcon,
        resolveBidStatusVariant,
        bidStateClass,
        refetchData,

        // Extra Filters
        stateFilter,
        typeFilter,
        statusFilter,
        customerOptions,
        stateOptions,
        countryOptions,
        departmentsOptions,
        statusOptions,
        typeOptions,
    };
}
